// src/pages/farmer/MachineryBankFarmer.js
// ✅ FINAL READY-TO-PASTE (Premium UI + Progress Stepper + Better Cards + MemberDocId Forwarding)
// ✅ NO existing logic removed (same filters + same steps + same data load)
// ✅ IMPORTANT FIX: forward memberDocId to apply page (for perfect member portal linking)
// ✅ FIX: products missing status now treated as approved (old data support)
// ✅ FIX: taluka filter supports serviceTalukas[] OR taluka (old + new)

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";

const cx = (...xs) => xs.filter(Boolean).join(" ");

// legacy → canonical
const CANONICAL_CATEGORY_MAP = {
  MACH_BANK: "MACH_BANK",
  MACHINERY: "MACH_BANK",
};
const toMachBank = (raw) => CANONICAL_CATEGORY_MAP[raw] || raw || "";

// ✅ FIX START: vendor safe name resolver (company_name missing)
const getVendorCompanyName = (v) => {
  return (
    v?.company_name ||
    v?.companyName ||
    v?.firmName ||
    v?.firm_name ||
    v?.profile?.companyName ||
    v?.profile?.company_name ||
    v?.profile?.firmName ||
    v?.name ||
    v?.profile?.name ||
    ""
  );
};
// ✅ FIX END

// ✅ NEW: Taluka matcher supports serviceTalukas[] + taluka
const vendorMatchesTaluka = (vendor, taluka) => {
  const t = String(taluka || "").trim();
  if (!t) return true;

  const arr = Array.isArray(vendor?.serviceTalukas) ? vendor.serviceTalukas : [];
  if (arr.length) {
    return arr.map((x) => String(x).trim()).includes(t);
  }
  return String(vendor?.taluka || "").trim() === t;
};

/* =========================
   STEP 1: Machinery Groups
========================= */
const COMPONENT_CARDS = [
  { id: "TRACTORS", label: "Tractors", icon: "🚜" },
  { id: "POWER_TILLERS", label: "Power Tillers", icon: "🛠️" },
  { id: "COMBINE_HARVESTERS", label: "Combine Harvesters", icon: "🌾" },
  { id: "KISAN_DRONE", label: "Kisan Drone / Spraying Drone", icon: "🛰️" },
  { id: "RICE_TRANSPLANTERS", label: "Rice / Paddy Transplanters", icon: "🌱" },
  { id: "SELF_PROPELLED_AGRI", label: "Self-propelled Agri / Hort Machinery", icon: "🚜" },
  { id: "LAND_DEV_TILLAGE", label: "Land Development & Tillage Implements", icon: "🪓" },
  { id: "SOWING_PLANTING", label: "Sowing / Planting / Seeding", icon: "🌾" },
  { id: "INTER_CULTIVATION", label: "Inter-cultivation Equipment", icon: "🌿" },
  { id: "HARVESTING_THRESHING", label: "Harvesting & Threshing", icon: "🧺" },
  { id: "RESIDUE_MANAGEMENT", label: "Residue / Hay / Forage Equipment", icon: "🔥" },
  { id: "MANUAL_ANIMAL", label: "Manual / Animal-drawn Implements", icon: "🐂" },
  { id: "POST_HARVEST_VALUE", label: "Post-harvest & Value Addition", icon: "🏭" },
  { id: "PLANT_PROTECTION", label: "Plant Protection (Sprayers etc.)", icon: "💧" },
  { id: "IRRIGATION_EQUIP", label: "Irrigation Equipments / Pumps", icon: "🚰" },
  { id: "SPECIALIZED_AGRI", label: "Specialized Agricultural Machinery", icon: "⚙️" },
  { id: "OTHERS", label: "Other Farm Machinery", icon: "🔧" },
];

const getGroupLabel = (id) => COMPONENT_CARDS.find((c) => c.id === id)?.label || id || "";

/* =========================
   UI atoms (premium)
========================= */
function StepPill({ step, active }) {
  return (
    <div
      className={cx(
        "flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-extrabold",
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-700 border-slate-200"
      )}
    >
      <span
        className={cx(
          "w-5 h-5 grid place-items-center rounded-full text-[11px]",
          active ? "bg-white/15" : "bg-slate-100"
        )}
      >
        {step}
      </span>
      {active ? "Active" : "Step"}
    </div>
  );
}

function HeaderCard({ title, sub, right }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-6 py-5 bg-gradient-to-r from-slate-950 via-slate-900 to-emerald-950 text-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-extrabold text-white/80">
              🚜 Shine Agri – Machinery Bank
            </div>
            <div className="mt-2 text-[20px] md:text-[26px] font-extrabold tracking-tight">
              {title}
            </div>
            <div className="mt-2 text-[12px] text-white/85 max-w-3xl">
              {sub}
            </div>
          </div>
          {right}
        </div>
      </div>
    </div>
  );
}

function SoftInfo({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-[12px] font-extrabold text-slate-900">{value}</div>
    </div>
  );
}

/* =========================
   MAIN
========================= */
export default function MachineryBankFarmer() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  const memberIdFromUrl = params.get("memberId") || "";
  const memberDocIdFromUrl = params.get("memberDocId") || ""; // ✅ NEW (forwarding)
  const stateFromUrl = params.get("state") || "all";
  const districtFromUrl = params.get("district") || "all";
  const talukaFromUrl = params.get("taluka") || "all";

  const [loading, setLoading] = useState(true);
  const [machines, setMachines] = useState([]);
  const [error, setError] = useState("");

  // steps: group → company → vendorBranch → model
  const [step, setStep] = useState("group");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedVendorKey, setSelectedVendorKey] = useState(null); // vendorId|branchLabel

  /* =========================
     LOAD (Approved vendors + approved products)
  ========================= */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        // 1) Approved vendors
        const vSnap = await getDocs(collection(db, "farmer_vendors"));
        const vMap = new Map();
        vSnap.forEach((d) => {
          const v = d.data() || {};
          if (v.status === "approved") vMap.set(d.id, { id: d.id, ...v });
        });

        // 2) Approved MACH_BANK products per vendor
        const all = [];
        for (const v of vMap.values()) {
          const pSnap = await getDocs(collection(db, "farmer_vendors", v.id, "products"));
          pSnap.forEach((d) => {
            const p = d.data() || {};
            const compId = p.componentId || toMachBank(p.category);

            if (compId !== "MACH_BANK") return;

            // ✅ FIX: if product has no status field, treat as APPROVED (old data compatibility)
            const pst = String(p.status ?? "approved").toLowerCase();
            if (pst !== "approved") return;

            all.push({
              id: d.id,
              vendorId: v.id,
              vendor: v,

              machGroup: p.machGroup || "",
              companyBrand: p.companyBrand || p.brand || "",
              branchLabel: p.branchLabel || "",

              componentId: compId,
              ...p,
            });
          });
        }

        setMachines(all);
      } catch (err) {
        console.error("MachineryBankFarmer load error:", err);
        setError(err.message || "Failed to load machinery data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* =========================
     Area Filter (taluka-wise)
  ========================= */
  const areaFiltered = useMemo(() => {
    return machines.filter((m) => {
      const st = String(m.vendor?.state || "").trim();
      const dist = String(m.vendor?.district || "").trim();

      if (stateFromUrl !== "all" && st !== stateFromUrl) return false;
      if (districtFromUrl !== "all" && dist !== districtFromUrl) return false;

      // ✅ FIX: support serviceTalukas[] OR taluka
      if (talukaFromUrl !== "all" && !vendorMatchesTaluka(m.vendor, talukaFromUrl)) return false;

      return true;
    });
  }, [machines, stateFromUrl, districtFromUrl, talukaFromUrl]);

  /* =========================
     Step 1: Group stats
  ========================= */
  const groupStats = useMemo(() => {
    return COMPONENT_CARDS.map((card) => {
      const list = areaFiltered.filter((m) => (m.machGroup || "OTHERS") === card.id);
      const vendorSet = new Set(list.map((m) => `${m.vendorId}|${m.branchLabel || ""}`));
      const companySet = new Set(
        list.map((m) => String(m.companyBrand || "").trim()).filter(Boolean)
      );
      return {
        ...card,
        machineCount: list.length,
        vendorCount: vendorSet.size,
        companyCount: companySet.size,
      };
    });
  }, [areaFiltered]);

  /* =========================
     Step 2: Companies for selected group
  ========================= */
  const companiesForGroup = useMemo(() => {
    if (!selectedGroup) return [];
    const set = new Set();
    areaFiltered.forEach((m) => {
      const g = m.machGroup || "OTHERS";
      if (g !== selectedGroup) return;
      const c = String(m.companyBrand || "").trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [areaFiltered, selectedGroup]);

  /* =========================
     Step 3: Vendors/Branches for selected group + company
  ========================= */
  const vendorBranchesForCompany = useMemo(() => {
    if (!selectedGroup || !selectedCompany) return [];
    const map = new Map();
    areaFiltered.forEach((m) => {
      const g = m.machGroup || "OTHERS";
      const c = String(m.companyBrand || "").trim();
      if (g !== selectedGroup) return;
      if (c !== selectedCompany) return;

      const key = `${m.vendorId}|${String(m.branchLabel || "").trim()}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          vendorId: m.vendorId,
          branchLabel: String(m.branchLabel || "").trim(),
          vendor: m.vendor,
          count: 0,
        });
      }
      map.get(key).count += 1;
    });

    return Array.from(map.values()).sort((a, b) => {
      const an = (getVendorCompanyName(a.vendor) || "").toLowerCase();
      const bn = (getVendorCompanyName(b.vendor) || "").toLowerCase();
      return an.localeCompare(bn) || String(a.branchLabel || "").localeCompare(String(b.branchLabel || ""));
    });
  }, [areaFiltered, selectedGroup, selectedCompany]);

  /* =========================
     Step 4: Models for selection
  ========================= */
  const modelsForSelection = useMemo(() => {
    if (!selectedGroup || !selectedCompany || !selectedVendorKey) return [];
    const [vendorId, branchLabel] = selectedVendorKey.split("|");

    return areaFiltered
      .filter((m) => {
        const g = m.machGroup || "OTHERS";
        const c = String(m.companyBrand || "").trim();
        const bl = String(m.branchLabel || "").trim();
        return (
          g === selectedGroup &&
          c === selectedCompany &&
          m.vendorId === vendorId &&
          bl === String(branchLabel || "")
        );
      })
      .sort((a, b) => String(a.modelName || "").localeCompare(String(b.modelName || "")));
  }, [areaFiltered, selectedGroup, selectedCompany, selectedVendorKey]);

  /* =========================
     Handlers
  ========================= */
  const selectGroup = (groupId, active) => {
    if (!active) return;
    setSelectedGroup(groupId);
    setSelectedCompany(null);
    setSelectedVendorKey(null);
    setStep("company");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToGroups = () => {
    setStep("group");
    setSelectedGroup(null);
    setSelectedCompany(null);
    setSelectedVendorKey(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectCompany = (company) => {
    setSelectedCompany(company);
    setSelectedVendorKey(null);
    setStep("vendor");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToCompanies = () => {
    setStep("company");
    setSelectedCompany(null);
    setSelectedVendorKey(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectVendorBranch = (key) => {
    setSelectedVendorKey(key);
    setStep("model");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToVendors = () => {
    setStep("vendor");
    setSelectedVendorKey(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleApply = (machine) => {
    const qp = new URLSearchParams();

    if (memberIdFromUrl) qp.set("memberId", memberIdFromUrl);

    // ✅ forward memberDocId (perfect linking to member dashboard)
    if (memberDocIdFromUrl) qp.set("memberDocId", memberDocIdFromUrl);

    qp.set("vendorId", machine.vendorId);
    qp.set("productId", machine.id);

    // keep area filters
    if (stateFromUrl !== "all") qp.set("state", stateFromUrl);
    if (districtFromUrl !== "all") qp.set("district", districtFromUrl);
    if (talukaFromUrl !== "all") qp.set("taluka", talukaFromUrl);

    navigate(`/farmer/machinery/apply?${qp.toString()}`);
  };

  /* =========================
     UI
  ========================= */

  const activeStepLabel =
    step === "group"
      ? "1) Group"
      : step === "company"
      ? "2) Company"
      : step === "vendor"
      ? "3) Vendor/Branch"
      : "4) Model";

  return (
    <div className="min-h-screen bg-[radial-gradient(1200px_520px_at_50%_-140px,#dbeafe_0%,#f1f5f9_45%,#ffffff_100%)] py-10">
      <div className="max-w-6xl mx-auto px-4">
        <HeaderCard
          title="Machinery Bank – Taluka-wise Company Listing"
          sub="Step 1: Machinery group → Step 2: Company → Step 3: Vendor/Branch → Step 4: Model."
          right={
            <div className="flex flex-col gap-2 items-end">
              <div className="flex items-center gap-2">
                <StepPill step="1" active={step === "group"} />
                <StepPill step="2" active={step === "company"} />
                <StepPill step="3" active={step === "vendor"} />
                <StepPill step="4" active={step === "model"} />
              </div>
              <div className="text-[11px] text-white/85 font-extrabold">
                Current: {activeStepLabel}
              </div>
            </div>
          }
        />

        {/* Info row */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <SoftInfo
            label="Area Filter"
            value={
              talukaFromUrl !== "all"
                ? `${talukaFromUrl}${districtFromUrl !== "all" ? `, ${districtFromUrl}` : ""}${
                    stateFromUrl !== "all" ? ` (${stateFromUrl})` : ""
                  }`
                : "All Areas"
            }
          />
          <SoftInfo label="Member" value={memberIdFromUrl ? memberIdFromUrl : "Guest"} />
          <SoftInfo label="Available Models" value={String(areaFiltered.length)} />
        </div>

        {error && (
          <div className="mt-4 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">
            ⚠ {error}
          </div>
        )}

        {loading ? (
          <div className="mt-6 text-sm text-slate-500">Loading machinery data…</div>
        ) : areaFiltered.length === 0 ? (
          <div className="mt-6 text-sm text-slate-500">
            No machinery vendors found for your area yet.
          </div>
        ) : (
          <>
            {/* STEP 1 */}
            {step === "group" && (
              <section className="mt-6">
                <div className="flex items-end justify-between gap-3 mb-3">
                  <div>
                    <h2 className="text-[14px] font-extrabold text-slate-900">
                      Step 1 – Select Machinery Group
                    </h2>
                    <p className="text-[11px] text-slate-600 mt-1">
                      Only groups with available models are clickable.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {groupStats.map((g) => {
                    const active = g.machineCount > 0;
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => selectGroup(g.id, active)}
                        className={cx(
                          "text-left rounded-2xl px-4 py-3 shadow-sm transition border",
                          active
                            ? "bg-white border-slate-200 hover:border-emerald-500 hover:shadow-md"
                            : "bg-slate-50 border-slate-200 cursor-not-allowed opacity-60"
                        )}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[18px]">{g.icon}</span>
                          <span className="text-[13px] font-extrabold text-slate-900">{g.label}</span>
                        </div>

                        <div className="mt-2 grid grid-cols-3 gap-2">
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
                            <div className="text-[9px] uppercase tracking-widest font-extrabold text-slate-500">
                              Models
                            </div>
                            <div className="text-[12px] font-extrabold text-slate-900">{g.machineCount}</div>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
                            <div className="text-[9px] uppercase tracking-widest font-extrabold text-slate-500">
                              Company
                            </div>
                            <div className="text-[12px] font-extrabold text-slate-900">{g.companyCount}</div>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
                            <div className="text-[9px] uppercase tracking-widest font-extrabold text-slate-500">
                              Vendors
                            </div>
                            <div className="text-[12px] font-extrabold text-slate-900">{g.vendorCount}</div>
                          </div>
                        </div>

                        <div className="mt-2 text-[11px] font-extrabold">
                          {active ? (
                            <span className="text-emerald-700">View companies ⟶</span>
                          ) : (
                            <span className="text-slate-400">Not available</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* STEP 2 */}
            {step === "company" && selectedGroup && (
              <section className="mt-6">
                <button
                  type="button"
                  onClick={backToGroups}
                  className="text-[11px] text-slate-500 hover:text-slate-900 underline"
                >
                  ⟵ Back to groups
                </button>

                <h2 className="text-[14px] font-extrabold text-slate-900 mt-2">
                  Step 2 – Select Company (for {getGroupLabel(selectedGroup)})
                </h2>
                <p className="text-[11px] text-slate-600 mb-3">
                  Only companies available in your area are shown.
                </p>

                {companiesForGroup.length === 0 ? (
                  <div className="text-xs text-slate-500">No companies found.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {companiesForGroup.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => selectCompany(c)}
                        className="text-left bg-white border border-slate-200 hover:border-emerald-500 rounded-2xl px-4 py-3 shadow-sm hover:shadow-md transition"
                      >
                        <div className="text-[13px] font-extrabold text-slate-900">{c}</div>
                        <div className="text-[11px] text-slate-500 mt-1">
                          View vendors / branches ⟶
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* STEP 3 */}
            {step === "vendor" && selectedGroup && selectedCompany && (
              <section className="mt-6">
                <button
                  type="button"
                  onClick={backToCompanies}
                  className="text-[11px] text-slate-500 hover:text-slate-900 underline"
                >
                  ⟵ Back to companies
                </button>

                <h2 className="text-[14px] font-extrabold text-slate-900 mt-2">
                  Step 3 – Select Vendor / Branch
                </h2>
                <p className="text-[11px] text-slate-600 mb-3">
                  Company: <b>{selectedCompany}</b> • Group: <b>{getGroupLabel(selectedGroup)}</b>
                </p>

                {vendorBranchesForCompany.length === 0 ? (
                  <div className="text-xs text-slate-500">No vendors found.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {vendorBranchesForCompany.map((vb) => (
                      <button
                        key={vb.key}
                        type="button"
                        onClick={() => selectVendorBranch(vb.key)}
                        className="text-left bg-white border border-slate-200 hover:border-sky-500 rounded-2xl px-4 py-3 shadow-sm hover:shadow-md transition"
                      >
                        <div className="text-[13px] font-extrabold text-slate-900">
                          {getVendorCompanyName(vb.vendor) || vb.vendor?.company_name || "Vendor"}
                        </div>
                        <div className="text-[11px] text-slate-600 mt-1">
                          {vb.vendor?.taluka ? `${vb.vendor.taluka}, ` : ""}
                          {vb.vendor?.district ? `${vb.vendor.district} ` : ""}
                          {vb.vendor?.state ? `(${vb.vendor.state})` : ""}
                        </div>

                        <div className="mt-2 flex items-center justify-between">
                          <div className="text-[11px] text-slate-500">
                            Branch: <b className="text-slate-900">{vb.branchLabel || "Default"}</b>
                          </div>
                          <span className="text-[11px] font-extrabold text-emerald-700">
                            Models: {vb.count}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* STEP 4 */}
            {step === "model" && selectedGroup && selectedCompany && selectedVendorKey && (
              <section className="mt-6">
                <button
                  type="button"
                  onClick={backToVendors}
                  className="text-[11px] text-slate-500 hover:text-slate-900 underline"
                >
                  ⟵ Back to vendors
                </button>

                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mt-2 mb-3">
                  <div>
                    <h2 className="text-[14px] font-extrabold text-slate-900">
                      Step 4 – Select Model
                    </h2>
                    <p className="text-[11px] text-slate-600">
                      Company: <b>{selectedCompany}</b> • Group: <b>{getGroupLabel(selectedGroup)}</b>
                    </p>
                  </div>

                  <div className="text-[11px] text-slate-600">
                    Showing <b>{modelsForSelection.length}</b> model(s)
                  </div>
                </div>

                {modelsForSelection.length === 0 ? (
                  <div className="text-xs text-slate-500">No models found.</div>
                ) : (
                  <div className="bg-white border border-black rounded-2xl overflow-hidden shadow-sm">
                    <div className="overflow-auto max-h-[520px]">
                      <table className="min-w-full text-[11px] border-collapse border border-black">
                        <thead className="bg-slate-100 sticky top-0 z-10">
                          <tr>
                            <Th>#</Th>
                            <Th>Company / Model</Th>
                            <Th>HP</Th>
                            <Th>Pricing (₹)</Th>
                            <Th>Branch</Th>
                            <Th>Action</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {modelsForSelection.map((m, idx) => (
                            <tr key={`${m.vendorId}-${m.id}`} className="hover:bg-slate-50">
                              <Td>{idx + 1}</Td>
                              <Td>
                                <div className="font-extrabold text-slate-900">
                                  {m.companyBrand || m.brand || "-"} {m.modelName || ""}
                                </div>
                                <div className="text-[10px] text-slate-600">
                                  {m.machineType || getGroupLabel(selectedGroup)}
                                </div>
                              </Td>
                              <Td>{m.hpRating || "—"}</Td>
                              <Td>
                                <div className="text-[10px] text-slate-800">
                                  {m.priceExShowroom ? `Ex: ₹${m.priceExShowroom}` : "Ex: —"}
                                </div>
                                <div className="text-[10px] text-slate-800">
                                  {m.onRoadPriceApprox ? `On-road: ₹${m.onRoadPriceApprox}` : "On-road: —"}
                                </div>
                              </Td>
                              <Td>{m.branchLabel || "Default"}</Td>
                              <Td>
                                <button
                                  type="button"
                                  onClick={() => handleApply(m)}
                                  className="px-3 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-extrabold border border-black hover:bg-emerald-700"
                                >
                                  Apply / Quotation
                                </button>
                              </Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* small helpers */
function Th({ children }) {
  return (
    <th className="px-2 py-2 text-[11px] font-extrabold text-slate-700 border border-black text-left">
      {children}
    </th>
  );
}
function Td({ children }) {
  return (
    <td className="px-2 py-1.5 text-[11px] text-slate-800 border border-black align-top">
      {children}
    </td>
  );
}
